'use client';

import React from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import {
  Content,
  Parent,
  Text,
  Heading,
  Emphasis,
  Strong,
  Paragraph,
  Link,
  FootnoteReference,
  List as MdList,
  ListItem as MdListItem,
} from 'mdast';
import { ChunkDocContext } from '../lib/parseMarkdown';
import WatermarkWrapper from './WatermarkWrapper';
import TimelineDiagram from './timeline/TimelineDiagram';
import Tooltip from '@/components/Tooltip';
import slugify from 'slugify';
import { panelData } from '@/panelData';
import { useLocale } from './contexts/LocaleContext';
import TabBoxes from './components/TabBoxes';
import BackUp from './BackUp';

import GPTScaleOriginal from './GPTScale';
import TrainingCurvesOriginal from './TrainingCurves';
import DeploymentCurvesOriginal from './DeploymentCurves';
import SoftwareProgressContributorsOriginal from './SoftwareProgressContributors';
import TimeHorizonOriginal from './TimeHorizon';
import TimeHorizonExtdOriginal from './TimeHorizonExtd';
import TimeHorizon80pOriginal from './TimeHorizon80p';
import KeyMetrics2026Original from './KeyMetrics2026';
import KeyMetrics2027Original from './KeyMetrics2027';
import ChinaComputeCentralizationOriginal from './ChinaComputeCentralization';
import ComputeAllocation2024v2027Original from './ComputeAllocation2024v2027';
import ComputeBreakdownOriginal from './ComputeBreakdown';
import ComputeGrowthOriginal from './ComputeGrowth';
import CapabilityTypesOriginal from './CapabilityTypes';
import ComputeUsers2527Original from './ComputeUsers2527';
import ComputeOwners2324Original from './ComputeOwners2324';
import ComputeOwners2527Original from './ComputeOwners2527';
import ComputeOwnersPieOriginal from './ComputeOwnersPie';
import ComputeUsersPieOriginal from './ComputeUsersPie';
import TakeoffMethodOriginal from './TakeoffMethod';
import TimelinesMethodOriginal from './TimelinesMethod';
import TakeoffSCSAROriginal from './TakeoffSCSAR';
import TakeoffSCtoASIOriginal from './TakeoffSCtoASI';
import ComputeOverviewOriginal from './ComputeOverview';
import ComputeProductionOriginal from './ComputeProduction';
import WeightsSecurityTimelineOriginal from './WeightsSecurityTimeline';
import SecretsSecurityTimelineOriginal from './SecretsSecurityTimeline';
import GlobalComputeOriginal from './GlobalCompute';
import CompanyComputeOriginal from './CompanyCompute';
import FinancialsOriginal from './Financials';
import PowerOriginal from './Power';
import NvidiaChipsOriginal from './NvidiaChips';
import InfChipsOriginal from './InfChips';
import ChipsInUseOriginal from './ChipsInUse';
import TeamProfiles from './TeamProfiles';
import { unescapeMarkdownForLaTeX } from '@/lib/utils';
import PublicationDate from './components/PublicationDate';
import Image from 'next/image';
import optimizedImages from '@/lib/optimizedImages';
import { ErrorBoundary } from 'react-error-boundary';


// Create watermarked versions of each component
const TimelinesMethod = WatermarkWrapper(TimelinesMethodOriginal);
const GPTScale = WatermarkWrapper(GPTScaleOriginal, 25, 30);
const TrainingCurves = WatermarkWrapper(TrainingCurvesOriginal);
const DeploymentCurves = WatermarkWrapper(DeploymentCurvesOriginal);
const SoftwareProgressContributors = WatermarkWrapper(SoftwareProgressContributorsOriginal, 10);
const TimeHorizon = WatermarkWrapper(TimeHorizonOriginal, 20, 10);
const TimeHorizonExtd = WatermarkWrapper(TimeHorizonExtdOriginal, 20, 10);
const TimeHorizon80p = WatermarkWrapper(TimeHorizon80pOriginal, 20, 10);
const KeyMetrics2026 = WatermarkWrapper(KeyMetrics2026Original, 25, 50);
const KeyMetrics2027 = WatermarkWrapper(KeyMetrics2027Original, 25, 50);
const ChinaComputeCentralization = WatermarkWrapper(ChinaComputeCentralizationOriginal);
const ComputeAllocation2024v2027 = WatermarkWrapper(ComputeAllocation2024v2027Original);
const ComputeBreakdown = WatermarkWrapper(ComputeBreakdownOriginal);
const ComputeGrowth = WatermarkWrapper(ComputeGrowthOriginal);
const CapabilityTypes = WatermarkWrapper(CapabilityTypesOriginal, 10);
const ComputeUsers2527 = WatermarkWrapper(ComputeUsers2527Original);
const ComputeOwners2324 = WatermarkWrapper(ComputeOwners2324Original);
const ComputeOwners2527 = WatermarkWrapper(ComputeOwners2527Original);
const ComputeOwnersPie = WatermarkWrapper(ComputeOwnersPieOriginal);
const ComputeUsersPie = WatermarkWrapper(ComputeUsersPieOriginal);
const TakeoffMethod = WatermarkWrapper(TakeoffMethodOriginal);
const TakeoffSCSAR = WatermarkWrapper(TakeoffSCSAROriginal, 10);
const TakeoffSCtoASI = WatermarkWrapper(TakeoffSCtoASIOriginal, 20, 20);
const ComputeOverview = ComputeOverviewOriginal;
const ComputeProduction = WatermarkWrapper(ComputeProductionOriginal);
const WeightsSecurityTimeline = WeightsSecurityTimelineOriginal;
const SecretsSecurityTimeline = SecretsSecurityTimelineOriginal;
const GlobalCompute = WatermarkWrapper(GlobalComputeOriginal);
const CompanyCompute = WatermarkWrapper(CompanyComputeOriginal);
const Financials = WatermarkWrapper(FinancialsOriginal);
const Power = WatermarkWrapper(PowerOriginal);
const NvidiaChips = WatermarkWrapper(NvidiaChipsOriginal, 20, 20);
const InfChips = WatermarkWrapper(InfChipsOriginal, 20, 40);
const ChipsInUse = WatermarkWrapper(ChipsInUseOriginal);

// Renders the translated version of an embedded chart when a locale provider
// (see translations/<locale>/Provider.tsx) is present, else the English default.
const LocaleChart = ({ name, Default }: { name: string; Default: React.ComponentType }) => {
  const locale = useLocale();
  const Component = locale?.components?.[name] ?? Default;
  return <Component />;
};

// Invisible anchor that carries the chart data for a scenario date; uses the
// translated panelData (event labels etc.) when a locale provider is present.
const DateMarkerAnchor = ({ dateString, dateHeaderString, ending }: { dateString: string; dateHeaderString?: string; ending?: 'race' | 'slowdown' }) => {
  const locale = useLocale();
  const activePanelData = locale?.panelData ?? panelData;
  const matchingEntry = activePanelData.find(([date, , dataPointEnding]) => ending ? (date === dateString && ending === dataPointEnding) : (date === dateString && dataPointEnding === undefined));
  return (
    <div
      className="scroll-mt-[50vh]"
      id={`${ending || 'narrative'}-${dateString}`}
      data-chart-date={dateString}
      data-chart-date-header={dateHeaderString}
      data-chart-extra={JSON.stringify(matchingEntry)}
      style={{ height: 0 }}
    />
  );
};

export type FootnoteHandler = React.FC<{ footnoteReference: FootnoteReference, docContext?: ChunkDocContext }>

interface MarkdownProps {
  nodes: Content[];
  docContext?: ChunkDocContext; // We'll pass footnotes / entire root, etc.,
  ending?: 'race' | 'slowdown'
  FootnoteHandler?: FootnoteHandler
}

// ------------------------------------------------------------------------
// Helper functions for heading ids

/**
 * Recursively extract and concatenate text content from a markdown node.
 */
function getTextFromNode(node: Content): string {
  if (node.type === 'text') {
    return (node as Text).value;
  }
  if (("children" in node) && node.children && Array.isArray(node.children)) {
    return node.children.map(getTextFromNode).join(' ');
  }
  return '';
}

// ------------------------------------------------------------------------

export function FootnoteRef({
  footnoteReference,
  docContext,
}: {
  footnoteReference: FootnoteReference;
  docContext?: ChunkDocContext;
}) {
  const prefix = docContext?.prefix;
  const identifier = prefix ? `${prefix}-${footnoteReference.identifier}` : footnoteReference.identifier;
  const definition = docContext?.footnotes.find((f) => f.identifier === footnoteReference.identifier);
  if (!definition) {
    // fallback if no definition is found
    return (
      <sup id={`footnote-${identifier}`}>
        <a href={`/footnotes#footnote-${identifier}`}>[{footnoteReference.identifier}]</a>
      </sup>
    );
  }

  const tooltipContent = (
    <div className="rounded-lg px-4 py-3 font-sans [&_p:first-of-type]:mt-0 [&_p:last-of-type]:mb-0 [&_p]:text-base [&_ol]:text-base [&_ol]:m-0 [&_li]:text-base [&_li]:m-0 text-justify">
      <span className="text-lg font-bold font-mono block float-left border border-white px-1 rounded-md mr-4 mb-2 mt-[6px]">
        #{footnoteReference.identifier}
      </span>
      <Markdown nodes={definition.children} docContext={docContext} FootnoteHandler={FootnoteRef} />
      <div className="w-full clear-both"></div>
    </div>
  );

  return (
    <Tooltip content={tooltipContent} placement="top" interactive maxWidth={500}>
      <sup className="relative text-[var(--accent)] cursor-pointer" id={`footnote-${identifier}`}>
        <a
          href={`/footnotes#footnote-${identifier}`}
          className="text-[var(--accent)] text-xs no-underline"
          onClick={(e) => {
            // Check if this is a touch device
            const isTouchDevice =
              'ontouchstart' in window ||
              navigator.maxTouchPoints > 0 ||
              /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

            // Prevent navigation on touch devices
            if (isTouchDevice) {
              e.preventDefault();
            }
          }}
        >
          {footnoteReference.identifier}
        </a>
      </sup>
    </Tooltip>
  );
}

export default function Markdown({ nodes, docContext, ending, FootnoteHandler = FootnoteRef }: MarkdownProps) {
  return <ErrorBoundary fallback={<></>}>{nodes.map((node, index) => <Node node={node} key={index} docContext={docContext} ending={ending} FootnoteHandler={FootnoteHandler} />)}</ErrorBoundary>;
}

/**
 * TableCell: A helper component to render table header (<th>) or table data (<td>) cells.
 * It will read custom merge attributes from node.data.hProperties if provided.
 */
function TableCell({
  node,
  docContext,
  as: Component = 'td',
  FootnoteHandler,
}: {
  node: any;
  docContext?: ChunkDocContext;
  as?: 'td' | 'th';
  FootnoteHandler: ({ footnoteReference, docContext }: { footnoteReference: FootnoteReference, docContext?: ChunkDocContext }) => React.ReactNode;
}) {
  // If merged cell info was added (via remark-directive or remark-attr),
  // it should be available in node.data.hProperties.
  const { colspan, rowspan, ...rest } = node?.data?.hProperties || {};

  // More detailed check for the special character
  let hasSpecialChar = false;
  let nodeValue = '';

  if (node.children &&
    node.children.length === 1 &&
    node.children[0].type === 'text') {
    nodeValue = node.children[0].value.trim();
    hasSpecialChar = nodeValue === '+';

    // If it's the special character, replace it with empty string
    if (hasSpecialChar) {
      node.children[0].value = '';
    }
  }

  let cellClass = "border border-gray-300 px-2 py-1";

  // Add specific styles for header cells
  if (Component === 'th' && !hasSpecialChar) {
    cellClass += " font-semibold text-center";
  }

  return (
    <Component
      colSpan={colspan}
      rowSpan={rowspan}
      {...rest}
      className={cellClass}
    >
      <Markdown nodes={node.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
    </Component>
  );
}

function Node({ node, docContext, ending, FootnoteHandler }: { node: Content; docContext?: ChunkDocContext, ending?: 'slowdown' | 'race', FootnoteHandler: ({ footnoteReference, docContext }: { footnoteReference: FootnoteReference, docContext?: ChunkDocContext }) => React.ReactNode }): React.ReactNode {

  switch (node.type as string) {
    case 'text': {
      const t = node as Text;
      return <React.Fragment>{t.value}</React.Fragment>;
    }
    case 'paragraph': {
      const p = node as Paragraph;
      return (
        <p>
          <Markdown nodes={p.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
        </p>
      );
    }
    case 'heading': {
      const h = node as Heading;
      const Tag = `h${h.depth}` as keyof JSX.IntrinsicElements;
      // Use an externally provided id if available, otherwise generate from heading text.
      const textContent = getTextFromNode(h);
      const id = slugify(textContent, { lower: true, strict: true });
      return (
        <Tag id={id}>
          <Markdown nodes={h.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
        </Tag>
      );
    }
    case 'strong': {
      const s = node as Strong;
      return (
        <strong>
          <Markdown nodes={s.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
        </strong>
      );
    }
    case 'emphasis': {
      const e = node as Emphasis;
      return (
        <em>
          <Markdown nodes={e.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
        </em>
      );
    }
    case 'link': {
      const l = node as Link;
      return (
        <a href={l.url}>
          <Markdown nodes={l.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
        </a>
      );
    }
    case 'image': {
      const image = node as { url: string; alt?: string; title?: string };
      const shouldHaveWatermark = !image.url.includes('-nowatermark');
      const shouldShiftRight = image.url.includes('-inexpandable');
      return (
        <span className="block flex flex-col items-center justify-center mb-4 mt-4 relative">
          <span className="relative">
          {optimizedImages[image.url] ? (
            <span className={`relative max-w-full h-auto ${shouldShiftRight ? 'ml-[-8px]' : 'ml-[-20px]'} py-4 px-2 ${image.alt && "mix-blend-multiply"}`}>
              <Image
                src={optimizedImages[image.url]}
                alt={image.alt || ""}
                title={image.title || ""}
                className={``}
                loading='lazy'
              />
            </span>
          ) : (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={image.url}
              alt={image.alt || ""}
              title={image.title || ""}
              className={`max-w-full h-auto ${shouldShiftRight ? 'ml-[-8px]' : 'ml-[-20px]'} py-4 px-2 ${image.alt && "mix-blend-multiply"}`}
            />
          )}
            {shouldHaveWatermark && (
              <span className="absolute bottom-1 right-5 flex items-center font-mono justify-center text-gray-400 pointer-events-none text-base">
                ai-2027.com
              </span>
            )}
          </span>
          <span className="block text-center text-sm font-mono">{image.title || image.alt || ""}</span>
        </span>
      );
    }
    case 'footnoteReference': {
      const fr = node as FootnoteReference;
      return <FootnoteHandler footnoteReference={fr} docContext={docContext} />;
    }
    case 'list': {
      const list = node as MdList;
      const children = <Markdown nodes={list.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />;
      return list.ordered ? (
        <ol className="pl-6">{children}</ol>
      ) : (
        <ul className="pl-6">{children}</ul>
      );
    }
    case 'listItem': {
      const li = node as MdListItem;
      return (
        <li className="[&>*:first-child]:mt-0 [&>*:last-child]:mb-0 my-1 [&>p+ul]:mt-0 [&>p+ol]:mt-0 [&>p:has(+ul)]:mb-0 [&>p:has(+ol)]:mb-0">
          <Markdown nodes={li.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
        </li>
      );
    }
    case 'table': {
      const table = node as any;
      if (!table.children || table.children.length === 0) return null;
      const headerRow = table.children[0];
      const bodyRows = table.children.slice(1);
      return (
        <div className="overflow-x-auto">
          <table className="table-auto border-collapse border border-gray-300 my-4">
            <thead className="bg-gray-100">
              <tr>
                {headerRow.children.map((cell: any, cellIndex: number) => (
                  <TableCell
                    key={cellIndex}
                    node={cell}
                    docContext={docContext}
                    as="th"
                    FootnoteHandler={FootnoteHandler}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row: any, rowIndex: number) => (
                <tr key={rowIndex}>
                  {row.children.map((cell: any, cellIndex: number) => (
                    <TableCell
                      key={cellIndex}
                      node={cell}
                      docContext={docContext}
                      as="td"
                      FootnoteHandler={FootnoteHandler}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case 'tableRow': {
      const row = node as any;
      return (
        <tr>
          {row.children.map((cell: any, i: number) => (
            <TableCell key={i} node={cell} docContext={docContext} as="td" FootnoteHandler={FootnoteHandler} />
          ))}
        </tr>
      );
    }
    case 'tableCell': {
      return <TableCell node={node} docContext={docContext} as="td" FootnoteHandler={FootnoteHandler} />;
    }
    case 'containerDirective': {
      const container = node as any;
      if (container.name === 'timeline') {
        return (
          <div style={{ width: container.width, height: container.height }}>
            <TimelineDiagram key={"timeline-diagram"} />
          </div>
        );
      } else if (container.name === 'dateMarker') {
        const value = container?.children
          .flatMap((child: any) => child.children)
          .map((child: any) => child.value)
          .join('\n');
        const [dateString, dateHeaderString] = value.split('\n').filter((v: string) => v.trim().length > 0);

        return (
          <DateMarkerAnchor dateString={dateString} dateHeaderString={dateHeaderString} ending={ending} />
        );
      } else if (container.name === 'tabBoxes') {
        return (
          <TabBoxes
            // eslint-disable-next-line react/no-children-prop
            children={container.children}
            docContext={docContext}
            FootnoteHandler={FootnoteHandler}
          />
        );
      } else if (container.name === 'gptScale') {
        return (
          <LocaleChart name="gptScale" Default={GPTScale} />
        );
      } else if (container.name === 'keyMetrics2026') {
        return (
          <LocaleChart name="keyMetrics2026" Default={KeyMetrics2026} />
        );
      } else if (container.name === 'keyMetrics2027') {
        return (
          <KeyMetrics2027 />
        );
      } else if (container.name === 'chinaComputeCentralization') {
        return (
          <LocaleChart name="chinaComputeCentralization" Default={ChinaComputeCentralization} />
        );
      } else if (container.name === 'ComputeAllocationMarch') {
        return (
          <LocaleChart name="ComputeAllocation2024v2027" Default={ComputeAllocation2024v2027} />
        );
      } else if (container.name === 'TakeoffMethod') {
        return (
          <TakeoffMethod />
        );
      } else if (container.name === 'TakeoffSCtoASI') {
        return (
          <TakeoffSCtoASI />
        );
      } else if (container.name === 'ComputeOverview') {
        return (
          <ComputeOverview />
        );
      } else if (container.name === 'ComputeProduction') {
        return (
          <ComputeProduction />
        );
      } else if (container.name === 'WeightsSecurityTimeline') {
        return (
          <WeightsSecurityTimeline />
        );
      } else if (container.name === 'WeightsSecurityTimelineOriginal') {
        return (
          <WeightsSecurityTimelineOriginal />
        );
      } else if (container.name === 'SecretsSecurityTimeline') {
        return (
          <SecretsSecurityTimeline />
        );
      } else if (container.name === 'BackUp') {
        return (
          <BackUp />
        );
      } else if (container.name === 'TimelinesMethod') {
        return (
          <TimelinesMethod />
        );
      } else if (container.name === 'TakeoffSCSAR') {
        return (
          <TakeoffSCSAR />
        );
      } else if (container.name === 'SoftwareProgressContributors') {
        return (
          <SoftwareProgressContributors />
        );
      } else if (container.name === 'TimeHorizon80p') {
        return (
          <TimeHorizon80p />
        );
      } else if (container.name === 'TimeHorizon') {
        return (
          <TimeHorizon />
        );
      } else if (container.name === 'TimeHorizonExtd') {
        return (
          <TimeHorizonExtd />
        );
      } else if (container.name === 'ComputeAllocation2024v2027') {
        return (
          <LocaleChart name="ComputeAllocation2024v2027" Default={ComputeAllocation2024v2027} />
        );
      } else if (container.name === 'ComputeBreakdown') {
        return (
          <ComputeBreakdown />
        );
      } else if (container.name === 'ComputeOwners2324') {
        return (
          <ComputeOwners2324 />
        );
      } else if (container.name === 'ComputeOwners2527') {
        return (
          <ComputeOwners2527 />
        );
      } else if (container.name === 'ComputeUsers2527') {
        return (
          <ComputeUsers2527 />
        );
      } else if (container.name === 'ComputeOwnersPie') {
        return (
          <ComputeOwnersPie />
        );
      } else if (container.name === 'ComputeUsersPie') {
        return (
          <ComputeUsersPie />
        );
      } else if (container.name === 'ComputeGrowth') {
        return (
          <ComputeGrowth />
        );
      } else if (container.name === 'CapabilityTypes') {
        return (
          <CapabilityTypes />
        );
      } else if (container.name === 'NvidiaChips') {
        return (
          <NvidiaChips />
        );
      } else if (container.name === 'InfChips') {
        return (
          <InfChips />
        );
      } else if (container.name === 'Financials') {
        return (
          <Financials />
        );
      } else if (container.name === 'Power') {
        return (
          <Power />
        );
      } else if (container.name === 'ChipsInUse') {
        return (
          <ChipsInUse />
        );
      } else if (container.name === 'trainingCurves') {
        return (
          <TrainingCurves />
        );
      } else if (container.name === 'DeploymentCurves') {
        return (
          <LocaleChart name="DeploymentCurves" Default={DeploymentCurves} />
        );
      } else if (container.name === 'GlobalCompute') {
        return (
          <GlobalCompute />
        );
      } else if (container.name === 'CompanyCompute') {
        return (
          <CompanyCompute />
        );
      } else if (container.name === 'teamProfiles') {
        return (
          <TeamProfiles>
            <Markdown nodes={container.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
          </TeamProfiles>
        );
      } else if (container.name === 'publicationDate') {
        return (
          <div className="hidden md:block">
            <LocaleChart name="publicationDate" Default={PublicationDate} />
          </div>
        );
      } else if (container.name === 'summary') {
        // Used to show preview of supplement on supplements page
        return <></>;
      }
      break;
    }
    case 'details': {
      const detailsNode = node as any;
      return (
        <details className="my-2 mr-3 rounded-lg px-2 py-1 border-[1.5px] border-[rgb(100,100,98)]">
          <summary>{detailsNode.summary}</summary>
          <div className="scroll-mt-20" id={slugify(detailsNode.summary, { lower: true, strict: true })}>
            <Markdown nodes={detailsNode.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
          </div>
        </details>
      );
    }
    case 'html': {
      const htmlNode = node as any;
      return <div dangerouslySetInnerHTML={{ __html: htmlNode.value }} />;
    }
    case 'inlineMath': {
      const inlineMath = node as any;
      try {
        const unescapedValue = unescapeMarkdownForLaTeX(inlineMath.value);
        const html = katex.renderToString(unescapedValue, { throwOnError: false });
        return <span dangerouslySetInnerHTML={{ __html: html }} />;
      } catch {
        return <span>{inlineMath.value}</span>;
      }
    }
    case 'math': {
      const math = node as any;
      try {
        const unescapedValue = unescapeMarkdownForLaTeX(math.value);
        const html = katex.renderToString(unescapedValue, { throwOnError: false, displayMode: true });
        return <div className="my-4" dangerouslySetInnerHTML={{ __html: html }} />;
      } catch {
        return <div>{math.value}</div>;
      }
    }
    default: {
      const parent = node as Parent;
      if (parent.children) {
        return (
          <div>
            <Markdown nodes={parent.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
          </div>
        );
      }
      return null;
    }
  }
}